import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "http://nissi-india.com";
// const LOGO_URL = "/assets/company-logo.png"; // change if needed

// ✅ safe helpers
const s = (v) => (v ?? "").toString().trim();
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

// ✅ support multiple query key names (because URLs vary while migrating ASP)
const getAnyParam = (params, keys, fallback = "") => {
  for (const k of keys) {
    const val = params.get(k);
    if (s(val)) return val;
  }
  return fallback;
};

const WeeklyBattaAbstractReportPrint = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  // ✅ accept different param names (your URL bar sometimes shows different names)
  const finYearId =
    getAnyParam(params, ["finYearId", "finyearid"]) || localStorage.getItem("finYearId") || "";

  const loginBranchId =
    getAnyParam(params, ["loginBranchId", "loginbranchid", "branchId", "branchid"]) ||
    localStorage.getItem("branchId") ||
    "";

  const branchId = getAnyParam(params, ["branchId", "branchid"], "Select") || "Select";

  const fromDate = getAnyParam(
    params,
    ["fromDate", "fromdate", "from", "abstractfrom"],
    ""
  );

  const toDate = getAnyParam(
    params,
    ["toDate", "todate", "to", "abstractto"],
    ""
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  // ✅ show error box like your screenshot
  const [error, setError] = useState("");

  const fetchPrintData = async () => {
    setError("");

    if (!s(finYearId)) {
      toast.warn("Missing finYearId.");
      setError("Missing finYearId.");
      return;
    }
    if (!s(loginBranchId)) {
      toast.warn("Missing loginBranchId.");
      setError("Missing loginBranchId.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/weekly-batta-abstract/print-data`, {
        params: {
          finYearId: toInt(finYearId),
          loginBranchId: toInt(loginBranchId),
          branchId: String(branchId || "Select"),
          fromDate: String(fromDate || ""),
          toDate: String(toDate || ""),
        },
        timeout: 20000,
      });

      if (res?.data?.success) {
        const list = res.data.rows || [];
        setRows(list);
        setGrandTotal(Number(res.data.grandTotal || 0));

        // ✅ print after render
        setTimeout(() => window.print(), 400);
      } else {
        const msg = res?.data?.message || "Failed to load print data.";
        toast.error(msg);
        setError(msg);
      }
    } catch (err) {
      // axios network errors show "Network Error" -> looks like "Failed to fetch"
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch";

      toast.error(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="printRoot">
      <ToastContainer position="top-right" autoClose={2200} />

      <div className="pageFrame">
        {/* HEADER */}
        <div className="reportHeader">
          <div className="logoBox">
            {/* <img
              src={LOGO_URL}
              alt="Company Logo"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            /> */}
          </div>

          <div className="companyBox">
            <div className="companyName">Nissi Infotech</div>
            <div className="companySub">Nissi</div>
            <div className="companySub2">CIVIL CONSTRUCTION</div>
            <div className="companyLine">
              3/42-12, Nagappa Gounder St, Kavundampalayam, Coimbatore-30, Tamil Nadu, India.
            </div>
            <div className="companyLine">Phone No : 9003744644</div>
            <div className="companyLine">infraconsoft-implementation@nissiinfotech.in</div>
          </div>
        </div>

        {/* TITLE */}
        <div className="titleBar">WEEKLY BATTA ABSTRACT REPORT</div>

        {/* ✅ ERROR BOX (like your screenshot) */}
        {error && (
          <div className="errorBox">
            <div className="errorTitle">Error loading report:</div>
            <div className="errorMsg">{error}</div>
            <button className="retryBtn" onClick={fetchPrintData} disabled={loading}>
              Retry
            </button>
          </div>
        )}

        {/* TABLE */}
        <div className="tableWrap">
          <table className="reportTable">
            <thead>
              <tr>
                <th className="wSno">S.No</th>
                <th className="wDept">Department</th>
                <th className="wAmt">Amount</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="noData">
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={idx}>
                    <td className="center">{idx + 1}</td>
                    <td className="center">{r.fld_department ?? r.Department ?? ""}</td>
                    <td className="center">{r.fld_receivedamount ?? r.Amount ?? ""}</td>
                  </tr>
                ))
              )}

              {/* TOTAL ROW */}
              <tr className="totalRow">
                <td className="blankCell"></td>
                <td className="totalLabel">Total</td>
                <td className="center totalValue">{grandTotal}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SIGN ROW */}
        <div className="signRow">
          <div>Prepared By</div>
          <div>Checked By</div>
          <div>Manager-Plants</div>
        </div>

        {loading && <div className="loadingNote">Loading print data...</div>}
      </div>

      <style>{`
        /* ROOT + PAGE */
        .printRoot {
          background: #eee;
          padding: 16px;
          display: flex;
          justify-content: center;
        }
        .pageFrame {
          background: #fff;
          border: 1px solid #333;
          width: 980px;
          padding: 16px;
        }
        .loadingNote {
          text-align: center;
          margin-top: 10px;
          font-size: 12px;
        }

        /* HEADER */
        .reportHeader {
          border: 1px solid #000;
          display: grid;
          grid-template-columns: 180px 1fr;
          align-items: center;
          padding: 10px;
          gap: 10px;
        }
        .logoBox {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .logoBox img {
          max-width: 160px;
          max-height: 90px;
          object-fit: contain;
        }
        .companyBox {
          text-align: center;
          line-height: 1.2;
        }
        .companyName { font-weight: 900; font-size: 20px; }
        .companySub { font-weight: 700; font-size: 12px; margin-top: 2px; }
        .companySub2 { font-weight: 800; font-size: 14px; margin-top: 2px; }
        .companyLine { font-size: 12px; margin-top: 2px; }

        /* TITLE BAR */
        .titleBar {
          border-left: 1px solid #000;
          border-right: 1px solid #000;
          border-bottom: 1px solid #000;
          text-align: center;
          padding: 10px 0;
          font-weight: 900;
          font-size: 18px;
          letter-spacing: 0.3px;
        }

        /* ERROR BOX */
        .errorBox {
          border: 1px solid #f2b8b8;
          background: #fdecec;
          padding: 14px;
          margin: 10px 0 14px 0;
        }
        .errorTitle {
          color: #b00020;
          font-weight: 900;
          margin-bottom: 4px;
        }
        .errorMsg {
          color: #b00020;
          font-weight: 700;
          margin-bottom: 10px;
        }
        .retryBtn {
          background: #d32f2f;
          color: #fff;
          border: none;
          padding: 8px 14px;
          border-radius: 6px;
          font-weight: 900;
          cursor: pointer;
        }
        .retryBtn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* TABLE */
        .tableWrap { overflow: visible !important; }
        .reportTable {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 14px;
        }
        .reportTable th,
        .reportTable td {
          border: 1px solid #000;
          padding: 14px 10px;
          vertical-align: middle;
        }
        .reportTable th { font-weight: 900; text-align: center; }
        .center { text-align: center; }

        .wSno { width: 200px; }
        .wDept { width: auto; }
        .wAmt { width: 360px; }

        .noData { text-align: center; padding: 18px; font-weight: 700; }

        .totalRow td { font-weight: 900; }
        .blankCell { background: #fff; }
        .totalLabel { text-align: center; font-weight: 900; }
        .totalValue { font-weight: 900; }

        /* SIGN ROW */
        .signRow {
          border: 1px solid #000;
          border-top: none;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          padding: 18px 14px;
          font-weight: 900;
          text-align: center;
        }

        /* PRINT */
        @media print {
          body { margin: 0; }
          .printRoot { background: #fff; padding: 0; }
          .pageFrame {
            width: auto;
            border: 1px solid #000;
            padding: 12px;
          }
          /* hide toast + error box in print */
          .Toastify { display: none !important; }
          .errorBox { display: none !important; }

          @page {
            size: A4 landscape;
            margin: 10mm;
          }
        }
      `}</style>
    </div>
  );
};

export default WeeklyBattaAbstractReportPrint;